import React from 'react';
import { motion } from 'framer-motion';
import { Section, Title, ContentBlock, Paragraph, Card, ListStyled, ListItem, Emphasis } from '../styled/StyledComponents';
import { contentVariants, itemVariants } from '../animations/Variants';
import styled from 'styled-components';

const FlexContainer = styled.div`
  display: flex;
  gap: 3rem;
  align-items: flex-start;
  margin: 2rem 0;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageWrapper = styled.div`
  flex: 1.2;
  position: sticky;
  top: 100px;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  max-height: 85vh;
  overflow-y: auto;
  padding-right: 1rem;
  
  /* Style de la barre de défilement */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    position: relative;
    top: 0;
    max-height: none;
    padding-right: 0;
  }
`;

const StyledImage = styled.img`
  width: 100%;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  aspect-ratio: 4/3;
  object-fit: cover;

  &:hover {
    transform: scale(1.02);
  }
`;

const TextContent = styled(ContentBlock)`
  flex: 1;
  padding-right: 2rem;
  
  @media (max-width: 768px) {
    padding-right: 0;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const StyledCard = styled(Card)`
  background: rgba(255, 255, 255, 0.9);
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-5px);
  }

  h3 {
    color: var(--color-primary);
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }
`;

const PsychomotriciteSection: React.FC = () => {
  return (
    <motion.div
      key="psychomotricite"
      initial="enter"
      animate="center"
      exit="exit"
      variants={contentVariants}
    >
      <Section>
        <Title>La Psychomotricité</Title>
        
        <FlexContainer>
          <TextContent>
            <Paragraph>
              La psychomotricité est une <Emphasis>discipline des sciences humaines</Emphasis> qui étudie 
              l'interaction constante entre nos fonctions motrices et notre vie psychique. Elle s'intéresse 
              à la façon dont notre corps exprime notre personnalité et nos émotions.
            </Paragraph>
            
            <CardsContainer>
              <StyledCard variants={itemVariants}>
                <h3>Développement Psychomoteur</h3>
                <Paragraph>
                  Accompagnement du développement des capacités motrices en lien avec les aspects cognitifs 
                  et émotionnels.
                </Paragraph>
              </StyledCard>

              <StyledCard variants={itemVariants}>
                <h3>Expression Corporelle</h3>
                <Paragraph>
                  Travail sur la conscience corporelle et l'expression des émotions à travers le mouvement 
                  et la relaxation.
                </Paragraph>
              </StyledCard>
            </CardsContainer>

            <ListStyled>
              <ListItem>L'unité corps-esprit comme base du développement humain</ListItem>
              <ListItem>L'importance du mouvement dans l'expression émotionnelle</ListItem>
              <ListItem>La relation entre développement moteur et psychique</ListItem>
            </ListStyled>
          </TextContent>

          <ImageWrapper>
            <StyledImage 
              src="/assets/images/psychomot.jpg" 
              alt="Séance de psychomotricité"
            />
            <StyledImage 
              src="/assets/images/psychomot2.jpg" 
              alt="Activité de psychomotricité"
            />
            <StyledImage 
              src="/assets/images/psychomot3.jpg" 
              alt="Exercice de psychomotricité"
            />
          </ImageWrapper>
        </FlexContainer>
      </Section>
    </motion.div>
  );
};

export default PsychomotriciteSection;
